import { Component, Input, OnDestroy, Inject, OnInit, Type, ElementRef, HostListener, Injector, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { trigger, style, transition, animate } from '@angular/animations';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { cloneDeep } from 'lodash';

import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { UserStateModel } from '../../../../Core/Users/models/user-state.model';
import { AccountModel } from '../../../../Core/Users/models/account.model';
import { LoginApi } from '../../../../api/login/login.api';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { CompressedNavComponent } from './CompressedNav/compressed-nav.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { UpsellDialogType, UpsellInfoDialogComponent } from '../../../Dialogs/components/UpsellInfoDialog/upsell-info-dialog.component';
import _ from 'underscore';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { UserFeaturesService } from '../../../../Common/Dashboard/services/user-features.service';
import { HubspotFormTypeEnum } from '../../../Misc/enums/hubspot-form-type.enum';
import { HubspotFormService } from '../../../Misc/services/hubspot-form.service';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';

export enum NavItemType {
    default,
    separator,
    accountList,
    contactSupport,
    dialog,
    takeOverPanel
}
/**
 * Content for the upsell popup shown when a locked sub item is clicked.
 * Maps onto the inputs of AnalysisUpsellPopupComponent.
 */
export interface NavItemUpsellPopup {
    icon?: string;
    heading: string;
    message: string;
    bullets: string[];
    endMessage?: string;
    featureName?: string;
    hubspotFormType?: HubspotFormTypeEnum;
    confirmationHeading?: string;
    confirmationMessage?: string;
}

export interface NavItem {
    type?: NavItemType;
    id: string;
    label?: string;
    subLabel?: string;
    icon?: string;
    ariaLabel?: string;

    url?: string;
    target?: string;
    itemSelectPath?: string;  // It is for detecting subItems in Nav bar.
    routerLink?: any;
    permissions?: userPermissionsEnum[];
    items?: NavItem[];
    collapsed?: boolean;
    isCollapsable?: boolean;
    disabledTooltip?: string;
    upsellMessage?: string;
    upsellPopup?: NavItemUpsellPopup;
    dialogReference?: Type<AbstractDialogComponent> | string;
    permissionsValidator?(permissions: userPermissionsEnum[]): boolean;
    showFunction?(userStateModel?: UserStateModel, configService?: ConfigService): boolean;
}

export interface NavItemInternal {
    type?: NavItemType;
    id: string;
    label?: string;
    subLabel?: string;
    icon?: string;
    ariaLabel?: string;

    url?: string;
    target?: string;
    routerLink?: any;
    itemSelectPath?: string;  // It is for detecting subItems in Nav bar.
    items?: NavItemInternal[];
    collapsed?: boolean;
    isCollapsable?: boolean;
    selected?: boolean;
    disabledTooltip?: string;
    dialogReference?: Type<AbstractDialogComponent> | string;
    showUpsell?: boolean;
    upsellMessage?: string;
    upsellPopup?: NavItemUpsellPopup;
    animating?: boolean;
}

@Component({
    selector: 'agpr-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    animations: [
        trigger('subMenu', [
            transition(':enter', [
                style({opacity: 0, height: 0}),
                animate('100ms 350ms ease-in-out', style({opacity: 1, height: '*'}))
            ]),
            transition(':leave', [
                style({opacity: 1, height: '*'}),
                animate('100ms ease-in-out', style({opacity: 0, height: 0}))
            ])
        ])
    ],
    standalone: false
})
export class NavbarComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    @Input() leftItems: NavItem[] = [];
    @Input() rightItems: NavItem[] = [];
    @Input() supportLinks: NavItem[] = [];

    @Input() logoUrl: string = null;
    @Input() logoRouterLink: string[];

    @ViewChild('leftSide') leftSide: ElementRef;
    @ViewChild('rightSide') rightSide: ElementRef;

    types = NavItemType;

    leftMenuItems: NavItemInternal[] = [];
    rightMenuItems: NavItemInternal[] = [];
    mainNavAccountItem: NavItemInternal;
    compressedNavAccountItem: NavItemInternal;
    accountSettingsItem: NavItemInternal;
    accountManagementItem: NavItemInternal;
    supportLinksItems: NavItemInternal[] = [];
    supportItem: NavItemInternal;
    selectedMenuItems: NavItemInternal[] = [];
    userAccounts: AccountModel[] = [];
    originalAccounts: AccountModel[] = [];

    // Needs special handling because it's an SVG
    paUrl: SafeResourceUrl;

    quickSearchPermitted: boolean = false;

    loading: boolean = false;

    private subscriptions: Subscription[] = [];
    public userState: UserStateModel;

    overlayContainer: Element;
    hiddenMenuItems: NavItemInternal[][] = [];
    private hiddenMenuItems$: BehaviorSubject<NavItemInternal[][]> = new BehaviorSubject<NavItemInternal[][]>(this.hiddenMenuItems);

    windowWidth: number;
    showCompressedNav: boolean = false;
    compressRightSide: boolean = false;
    compressLeftSide: boolean = false;
    elementsMeasured: boolean = false;
    leftSideWidth: number;
    rightSideWidth: number;
    compressedRightSideWidth: number;
    readonly logoAndMargins = 308; // 258px logo + 50px margins
    // Matches the dimensions of AnalysisUpsellPopupComponent so it can be positioned against the cursor
    readonly upsellPopupWidth = 485;
    readonly upsellPopupHeight = 372;
    readonly quickSearchWidth = 301;

    logoAltText: string;
    logoAltTextMap: Map<string, string> = new Map([
        [ConfigService.burrellesLuceGUID, 'Core.login.burrelles.logoAltText'],
        [ConfigService.lexisNexisGUID, 'Core.login.lexis.logoAltText'],
        [ConfigService.newsDirect, 'Core.login.newsdirect.logoAltText'],
        [ConfigService.paMedia, 'Core.login.paMedia.logoAltText']
    ]);
    isPAAccount: boolean = false;

    switchAccountId: number;
    // At this moment, in case we have both Account Managment and Account Management (New), User Management and User Management (New) at the same time.
    // "oldUrl" and "isJumpByRouter" are used to determine if the current URL was navigated through "href" or "routerLink".
    // These can be removed in the future when we exclusively use the new routing schema (and have only one Account Management and one User Management).
    private oldUrl: string;
    private isJumpByRouter: boolean = false;
    searchTerm?: string;

    readonly mediaIntelligenceId = 'primaryNavMediaIntelligence';
    readonly mediaRelationsId = 'primaryNavMediaRelations';
    readonly socialId = 'primaryNavSocial';

    constructor(
        private router: Router,
        private configService: ConfigService,
        private currentUserStateService: CurrentUserStateService,
        public coreDialogService: CoreDialogService,
        private loginApi: LoginApi,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public injector: Injector,
        public ref: ElementRef,
        public sanitizer: DomSanitizer,
        private userFeaturesService: UserFeaturesService,
        private hubspotFormService: HubspotFormService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getState().subscribe(
                userState => {
                    this.userState = userState;
                    this.userAccounts = cloneDeep(this.userState?.accounts) ?? [];
                    this.userAccounts = this.userAccounts?.filter(x => x.hasUiAccess);
                    this.userAccounts?.sort(this.sortActiveAccounts);
                    this.originalAccounts = this.userAccounts?.slice();
                    this.updateNavigation();
                }
            ),
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
                // Detect if it is jump through "routerLink" or "herf" in <a>
                if(this.oldUrl && this.oldUrl !== this.router.url){
                    this.isJumpByRouter = true;
                }
                this.oldUrl = this.router.url;
                this.setPrimaryNavSelectedItems();
                this.hideAllSubMenus();
            })
        );
    }

    @HostListener('window:resize')
    updateWidth() {
        this.windowWidth = window.innerWidth;
        this.addItemsToCompressedNav();
    }

    ngOnInit(): void {
        this.updateNavigation();
        this.overlayContainer = document.getElementById('main-content');
        if (this.userAccounts?.length) {
            this.originalAccounts = [...this.userAccounts];
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions.length = 0;
    }

    sortActiveAccounts(a, b) {
        if ((a.active && !a.isExpiryDatePast) && (!b.active || b.isExpiryDatePast)) {
            return -1;
        }
        if ((!a.active || a.isExpiryDatePast) && (b.active && !b.isExpiryDatePast)) {
            return 1;
        }
        return a.name.localeCompare(b.name);
    }

    filterAccounts() {
        const searchTerm = this.searchTerm?.toLowerCase();

        this.userAccounts = this.originalAccounts
            .filter(a => a.name.toLowerCase().includes(searchTerm))
            .sort((a, b) => {
                if ((a.active && !a.isExpiryDatePast) !== (b.active && !b.isExpiryDatePast)) {
                    return (a.active && !a.isExpiryDatePast) ? -1 : 1;
                }
                if (a.name.toLowerCase().startsWith(searchTerm) !== b.name.toLowerCase().startsWith(searchTerm)) {
                    return a.name.toLowerCase().startsWith(searchTerm) ? -1 : 1;
                }

                if (a.name.toLowerCase().includes(searchTerm) !== b.name.toLowerCase().includes(searchTerm)) {
                    return a.name.toLowerCase().includes(searchTerm) ? -1 : 1;
                }

                return a.name.localeCompare(b.name);
            });
    }

    /**
     * Verifies the permissions for a menu item
     * NOTE: if any of the menu item permissions are active, then the menu item will be considered active
     */
    private isActiveMenuItem(item: NavItem, activePermissions: userPermissionsEnum[]): boolean {
        if (item.showFunction && !item.showFunction(this.userState, this.configService)) {
            return false;
        }

        if (item.permissionsValidator) {
            return item.permissionsValidator(activePermissions);
        }

        if (!item.permissions || item.permissions.length < 1) {
            return true;
        }

        return !!item.permissions.find((permission) => {
            return activePermissions.indexOf(permission) > -1;
        });
    }

    /**
     * Maps primary nav config items to menu items for primeNG
     */
    private mapMenuItems(
        navItems: NavItem[],
        permissions: userPermissionsEnum[]
    ): NavItem[] {
        const menuItems = [];
        for (let i = 0, l = navItems?.length; i < l; i++) {
            const navItem = navItems[i];
            const activeMenuItem = this.isActiveMenuItem(navItem, permissions);

            // Items without a disabled tooltips or upsell messages should just be hidden altogether
            // Hide influencer streams if Media Relations not provisioned, niche case for when newswire/newsroom provisioned w/out Media Relations module
            if (
                !activeMenuItem &&
                (
                    (!navItem.disabledTooltip && !navItem.upsellMessage)
                    || (navItem.permissions?.includes(userPermissionsEnum.influencerStreams) && !permissions.includes(userPermissionsEnum.advancedTargeting))
                )

            ) {
                continue;
            }

            const menuItem: NavItemInternal = {
                type: navItem.type,
                label: navItem.label,
                subLabel: navItem.subLabel,
                icon: navItem.icon,
                ariaLabel: navItem.ariaLabel,
                id: '' + navItem.id,
                routerLink: navItem.routerLink,
                itemSelectPath: navItem.itemSelectPath,
                url: navItem.url,
                collapsed: navItem.collapsed,
                isCollapsable: navItem.isCollapsable !== undefined ? navItem.isCollapsable : true,
                disabledTooltip : !activeMenuItem ? navItem.disabledTooltip : null,
                dialogReference: navItem.dialogReference,
                target: navItem.target,
                showUpsell: !activeMenuItem && !!navItem.upsellMessage,
                upsellMessage: navItem.upsellMessage,
                upsellPopup: navItem.upsellPopup
            };

            if (navItem.items && navItem.items.length > 0) {
                menuItem.items = this.mapMenuItems(navItem.items, permissions);
                menuItem.collapsed = true;
            }

            menuItems.push(menuItem);
        }

        return menuItems;
    }

    /**
     * Recursively finds all the active items
     */
    private findActiveItems(items: NavItem[]): NavItem[] {
        const path = this.router.url;
        const pathItems = [];

        // Causes longer paths to be checked first, in case some menu items match
        // multiple path prefixes.  We want to match the most precise one first.
        let sortedItems = [...items].sort((x1, x2) => {
            if(this.isJumpByRouter){ // if it is navigated through router, items with routerLink has higher priority
                if (x1.routerLink && !x2.routerLink) {
                    return -1;
                } else if (!x1.routerLink && x2.routerLink) {
                    return 1;
                }
            }
            // A more reliable solution may be to sort by the number of slashes
            if (x1.routerLink && x2.routerLink) {
                return (<string> x2.routerLink).length - (<string> x1.routerLink).length;
            }
            return 0;
        });

        const hasItemSelect = [...items].filter(item => item.itemSelectPath);
        sortedItems = hasItemSelect.concat(sortedItems);

        for (let i = 0, l = sortedItems.length; i < l; i++) {
            const item = sortedItems[i];

            if (item.items) {
                const subItems = this.findActiveItems(item.items as NavItem[]);
                if (subItems.length > 0) {
                    pathItems.push(...subItems, item);

                    break;
                }
            }
            if ((item.itemSelectPath && path.indexOf(item.itemSelectPath) > -1 && path.indexOf('results') > -1) ||
                (item.routerLink && !item.itemSelectPath && path.indexOf(item.routerLink) > -1) ||
                (item.routerLink && (path === item.routerLink)) ||
                (item.url && path.indexOf(item.url) > -1)
            ) {
                pathItems.push(item);

                break;
            }
        }
        return pathItems;
    }

    /**
     * Selects the primary nav items based on the path
     */
    private setPrimaryNavSelectedItems(): void {
        if (!this.leftMenuItems) {
            return;
        }

        this.selectedMenuItems.forEach((item) => {
            (<any>item).selected = false;
        });

        this.selectedMenuItems = this.findActiveItems(this.leftMenuItems);

        if (this.selectedMenuItems.length < 1) {
            this.selectedMenuItems = this.findActiveItems(this.rightMenuItems);
            if (this.mainNavAccountItem) {
                this.mainNavAccountItem.selected = this.selectedMenuItems.some(item => item.id === 'primaryNavAccounts');
            }
            if (this.compressedNavAccountItem) {
                this.compressedNavAccountItem.selected = this.selectedMenuItems.some(item => item.id === 'primaryNavAccounts');
            }
        }

        this.selectedMenuItems.forEach((item) => {
            (<any>item).selected = true;
        });

        this.addItemsToCompressedNav();
    }

    /**
     * Sets the navigation items based on the current accounts and permissions
     */
    private updateNavigation(): void {
        if (!this.userState) {
            this.clearNavigation();
            return;
        }
        const userPermissions = this.userState.permissions;
        const adminPermissions = [
            ...this.userState.permissions
        ];

        if (this.userState.impersonator) {
            // combine the impersonator permissions with the user permissions
            // so you have access to ghost-in even if the user you ghosted into does not
            if (this.userState.impersonator.permissions.indexOf(userPermissionsEnum.accountGhostIn) !== -1) {
                adminPermissions.push(userPermissionsEnum.accountGhostIn);
            }
        }

        this.leftMenuItems = this.mapMenuItems(this.leftItems, userPermissions);

        this.rightMenuItems = this.mapMenuItems(this.rightItems, adminPermissions);

        this.createMainNavAccountItem();
        this.createCompressedNavAccountItem();

        this.replaceAccountItem(this.mainNavAccountItem);

        this.quickSearchPermitted = !!userPermissions.find(permission => permission === userPermissionsEnum.advancedTargeting);

        this.supportLinksItems = this.mapMenuItems(this.supportLinks, adminPermissions);
        this.supportItem = {
            id: 'primaryNavSupport',
            label: 'Core.navigation.admin.support',
            icon: 'fa-duotone fa-regular fa-circle-question',
            items: this.supportLinksItems,
            collapsed: true
        };

        if (this.supportItem && this.supportItem.items && this.supportItem.items.length > 0) {
            this.rightMenuItems.push(this.supportItem);
        }

        this.setPrimaryNavSelectedItems();
        this.updateWidth();

        if (this.configService.hasBrandGUID()) {
            this.hideNavItemsForWLPartners();
            this.logoAltText = this.logoAltTextMap.get(this.configService.getBrandGUID());
            this.isPAAccount = this.configService.brandGUIDIsPaMedia();
        } else {
            this.logoAltText = ''; // Agility PR Solutions
        }
        this.userFeaturesService.setUserFeatures(this.leftMenuItems);
    }

    // Creates new account item sorted for main nav
    createMainNavAccountItem() {
        const accountItem = this.rightMenuItems.filter(item => item.id === 'primaryNavAccounts')[0];
        this.accountSettingsItem = {
            id: 'primaryNavAccountSettings',
            items: accountItem?.items.filter(item => !item.items && item.type !== this.types.accountList) || []
        };
        this.accountManagementItem = {
            id: 'primaryNavManagement',
            items: accountItem?.items.filter(item => !!item.items && item.items?.length > 0) || []
        };
        this.mainNavAccountItem = {
            id: 'primaryNavAccounts',
            label: 'Core.navigation.account',
            items: [
                this.accountSettingsItem,
                this.accountManagementItem,
                {
                    type: this.types.accountList,
                    id: 'primaryNavAccountList'
                }
            ],
            collapsed: true
        };
    }

    // Creates new account item sorted for compressed nav
    createCompressedNavAccountItem() {
        const accountItems = [...this.accountSettingsItem.items];
        const userPrefsIndex = accountItems.findIndex(item => item.id === 'primaryNavUserPreferences');
        accountItems.splice(userPrefsIndex + 1, 0, ...this.accountManagementItem.items);
        this.compressedNavAccountItem = {
            id: 'primaryNavAccounts',
            label: 'Core.navigation.account',
            items: accountItems,
            collapsed: true
        };
    }

    replaceAccountItem(accountItem: NavItemInternal) {
        const accountItemIndex = this.rightMenuItems.findIndex(item => item?.id === 'primaryNavAccounts');
        this.rightMenuItems.splice(accountItemIndex, 1, accountItem);
    }

    hideNavItemsForWLPartners() {
        if (this.configService.brandGUIDIsPaMedia() || this.configService.brandGUIDIsRefMedia()) {
            this.leftMenuItems = this.leftMenuItems.filter(item => !item.showUpsell || item.id !== this.mediaRelationsId);
        } else if (this.configService.brandGUIDIsLexisNexis() || this.configService.brandGUIDIsUnicepta() || this.configService.brandGUIDIsKantar()) {
            this.leftMenuItems = this.leftMenuItems.filter(item => !item.showUpsell || (item.id !== this.socialId && item.id !== this.mediaIntelligenceId));
        }
    }

    /**
     * Sets the default version
     * of the navigation items
     */
    private clearNavigation(): void {
        this.leftMenuItems = [];
        this.rightMenuItems = [];
        this.selectedMenuItems = [];
    }

    closeDropDowns(item: NavItemInternal) {
        item.items?.forEach(element => {
            if (element.isCollapsable !== false && element?.collapsed === false){
                element.collapsed = true;
            }
        });
    }

    showSubMenu(item: NavItemInternal) {
        item.collapsed = false;
    }

    hideSubMenu(item: NavItemInternal) {
        if (item.isCollapsable !== false) {
            item.collapsed = true;
        }
        this.closeDropDowns(item);
    }

    hideAllSubMenus() {
        this.leftMenuItems.filter(item => !!item).forEach(item => item.collapsed = true);
        this.rightMenuItems.filter(item => !!item).forEach(item => item.collapsed = true);
    }

    /*
     * Open Intercom Live Chat
     */
    openIntercom() {
        window.Intercom('show');
    }

    switchAccount(account: AccountModel) {
        this.loading = true;
        this.switchAccountId = account.id;

        this.currentUserStateService.switchAccount(account.id).subscribe({
            next: (token) => {
                if (this.currentUserStateService.accountHasJournodex(account)) {
                    this.currentUserStateService.navigateToJournodex();
                    return;
                }

                window.location.href = window.location.origin + this.appBaseHref + 'Dashboard';
            },
            error: (error) => {
                this.loading = false;
            }
        });
    }

    openTakeOverPanel(component) {
        this.hideAllSubMenus();
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer
            }
        };
        this.coreDialogService.open(component, io);
    }

    openUpsellInfoPanel(item: NavItemInternal) {
        this.hideSubMenu(item);
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        let type: UpsellDialogType;
        switch (item.id) {
            case this.mediaIntelligenceId:
                type = UpsellDialogType.monitor;
                break;
            case this.mediaRelationsId:
                type = UpsellDialogType.discover;
                break;
            case this.socialId:
                type = UpsellDialogType.social;
                break;
        }
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                type: type
            }
        };
        this.coreDialogService.open(UpsellInfoDialogComponent, io);
    }

    /**
     * Sub items whose feature is not provisioned stay in the menu behind a lock and open an upsell
     * popup instead of navigating. The popup lives outside of Common, so it is opened by its map key.
     */
    openSubItemUpsell(subItem: NavItemInternal, event: Event) {
        event.preventDefault();
        event.stopPropagation();

        if (!subItem.upsellPopup) {
            return;
        }

        this.hideAllSubMenus();

        // TODO: NEP-38233 - remove this and requestUnlockDirectly() once we have marketing message
        // content for Visibility Intelligence
        if (subItem.upsellPopup.featureName === 'Visibility Intelligence') {
            this.requestUnlockDirectly(subItem.upsellPopup);
            return;
        }

        const ioModel = this.coreDialogService.getIoConfigCentered(this.upsellPopupWidth, this.upsellPopupHeight);

        // Anything the config leaves out keeps the popup's own default
        Object.entries(subItem.upsellPopup).forEach(([input, value]) => {
            if (value !== undefined) {
                ioModel.inputs[input] = value;
            }
        });

        this.coreDialogService.open('AnalysisUpsellPopupComponent', ioModel);
    }

    /**
     * Bypasses AnalysisUpsellPopupComponent's informational content entirely and requests the
     * unlock right away instead — used for Visibility Intelligence only.
     */
    private requestUnlockDirectly(upsellPopup: NavItemUpsellPopup) {
        if (!upsellPopup.hubspotFormType || !upsellPopup.featureName) {
            return;
        }

        this.hubspotFormService.requestUnlock(upsellPopup.hubspotFormType, upsellPopup.featureName).then(submitted => {
            if (submitted) {
                this.coreDialogService.open(
                    ConfirmDialogComponent,
                    {
                        inputs: {
                            heading: upsellPopup.confirmationHeading,
                            messageHtml: upsellPopup.confirmationMessage,
                            showCancel: false,
                            width: 600
                        }
                    }
                );
            }
        }).catch(() => {
            window.open('https://www.agilitypr.com/contact/');
        });
    }

    openCompressedNav() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                menuItems$: this.hiddenMenuItems$.asObservable(),
                userState: this.userState,
                userAccounts: this.userAccounts
            }
        };

        this.coreDialogService.open(CompressedNavComponent, ioModel, this.injector);
    }

    addItemsToCompressedNav() {
        if (!this.elementsMeasured) {
            this.leftSideWidth = this.leftSide?.nativeElement.getBoundingClientRect().width;
            this.rightSideWidth = this.rightSide?.nativeElement.getBoundingClientRect().width;
            this.compressedRightSideWidth = 32; // Compressed nav button

            if (this.quickSearchPermitted) {
                this.compressedRightSideWidth += this.quickSearchWidth;
            }

            if (this.leftSideWidth > 0 && this.rightSideWidth > 0) {
                this.elementsMeasured = true;
            } else {
                return;
            }
        }

        let totalContentWidth = this.logoAndMargins + this.leftSideWidth + this.rightSideWidth;

        this.hiddenMenuItems = [];
        if (this.windowWidth < totalContentWidth) {
            this.showCompressedNav = true;
            this.compressRightSide = true;

            this.replaceAccountItem(this.compressedNavAccountItem);

            const bottomMenuItems = [...this.rightMenuItems];
            if (bottomMenuItems?.length > 0) {
                bottomMenuItems.forEach(item => {
                    if (item) {
                        item.collapsed = true;
                        item.isCollapsable = true;
                    }
                });
            }
            this.hiddenMenuItems.push(bottomMenuItems);
        } else {
            this.compressRightSide = false;
            this.replaceAccountItem(this.mainNavAccountItem);
        }

        totalContentWidth = this.logoAndMargins + this.leftSideWidth + this.compressedRightSideWidth;

        if (this.windowWidth < totalContentWidth) {
            this.showCompressedNav = true;
            this.compressLeftSide = true;
            this.leftMenuItems?.forEach(item => item.collapsed = true);
            this.hiddenMenuItems.unshift(this.leftMenuItems);
        } else {
            this.compressLeftSide = false;
        }

        if (this.hiddenMenuItems.length === 0) {
            this.showCompressedNav = false;
        }

        this.hiddenMenuItems$.next(this.hiddenMenuItems);
    }

    toggleSubMenu(item: NavItemInternal) {
        if (item.animating) {
            return;
        }
        if (item.collapsed) {
            this.showSubMenu(item);
        } else {
            this.hideSubMenu(item);
        }
    }

    onFocusOut(event: FocusEvent, item: NavItem) {
        if (
            !!event.relatedTarget
            && !(event.relatedTarget as HTMLElement).classList?.contains('subitem')
            && ((event.relatedTarget as HTMLElement).tagName !== 'INPUT' || (event.relatedTarget as HTMLElement).classList.contains('p-autocomplete-input'))
        ) {
            this.hideSubMenu(item);
        }
    }
}

